
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import PricingCard from '@/components/pricing/PricingCard';
import SeoOnboardingForm from '@/components/forms/SeoOnboardingForm';
import { useToast } from '@/hooks/use-toast';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, ChevronRight, HelpCircle } from 'lucide-react';

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [additionalLocations, setAdditionalLocations] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const savedData = localStorage.getItem("seoOnboardingData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      const savedTime = new Date(parsedData.timestamp).getTime();
      const currentTime = new Date().getTime();
      const hoursSinceSaved = (currentTime - savedTime) / (1000 * 60 * 60);
      
      if (hoursSinceSaved < 24) {
        toast({
          title: "Form data restored",
          description: "We've restored your previous application data.",
        });
        setSelectedPlan(parsedData.selectedPlan);
        setShowOnboarding(true);
      } else {
        localStorage.removeItem("seoOnboardingData");
      }
    }
  }, [toast]);

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    setShowOnboarding(true);
  };

  const handleOnboardingSubmit = async (data: any) => {
    console.log('Form submitted:', { plan: selectedPlan, ...data });
    
    toast({
      title: "Application Submitted!",
      description: "We'll get back to you within 24 hours to discuss next steps.",
    });
    
    setShowOnboarding(false);
  };

  const pricingData = [
    {
      id: 'local',
      title: 'Local SEO',
      price: '$950',
      description: 'Focused strategies to improve local search visibility and drive neighborhood traffic.',
      features: [
        '1 location included',
        'Google Business Profile optimization',
        'Local keyword research and targeting',
        'Review generation strategy',
        'Local citation building',
        'Local content development',
        'Competitor analysis',
        'Monthly reporting'
      ],
      isPopular: false,
      color: 'bg-sky-gradient',
      link: '/services/local-seo'
    },
    {
      id: 'medium',
      title: 'Medium Competitive Keywords',
      price: '$2,000',
      description: 'Balanced approach for businesses in moderately competitive markets seeking growth.',
      features: [
        'Up to 3 locations included*',
        'Medium competition keyword targeting',
        'Content creation and optimization',
        'On-page SEO implementation',
        'Technical SEO audit and fixes',
        'Link building strategy',
        'Performance tracking dashboard',
        'Bi-weekly strategy calls'
      ],
      isPopular: true,
      color: 'bg-desert-gradient',
      link: '/services/competitive-keywords'
    },
    {
      id: 'ecommerce',
      title: 'E-commerce SEO',
      price: '$3,500+',
      description: 'Comprehensive SEO solutions for online stores and e-commerce platforms.',
      features: [
        'Product page optimization',
        'Category page optimization',
        'Schema markup implementation',
        'Shopping feed optimization',
        'Conversion rate optimization',
        'Marketplace integration',
        'Advanced analytics tracking'
      ],
      isPopular: false,
      color: 'bg-indigo-gradient',
      link: '/services/ecommerce-seo'
    },
    {
      id: 'specialized',
      title: 'Specialized Industries',
      price: '$5,000+',
      description: 'Tailored SEO services for legal, insurance, and other highly competitive industries.',
      features: [
        'Industry-specific keyword optimization',
        'Compliance-aware content strategy',
        'Authority building campaigns',
        'Expert content development',
        'Competitive analysis',
        'ROI tracking',
        'Dedicated account manager'
      ],
      isPopular: false,
      color: 'bg-canyon-gradient',
      link: '/services/specialized-industries'
    },
    {
      id: 'enterprise',
      title: 'Enterprise Solutions',
      price: 'Custom Quote',
      description: 'Enterprise-grade SEO solutions for large organizations with complex requirements.',
      features: [
        'Custom SEO strategy',
        'Multiple location management',
        'International SEO',
        'API integrations',
        'Advanced reporting suite',
        'Priority support',
        '24/7 monitoring'
      ],
      isPopular: false,
      color: 'bg-cosmic-gradient',
      link: '/services/enterprise-seo'
    }
  ];

  const faqs = [
    {
      question: "What's included in the initial SEO audit?",
      answer: "Our comprehensive initial audit includes technical website analysis, content evaluation, competitor research, backlink profile assessment, and keyword opportunity identification. We'll deliver a detailed report with actionable recommendations prioritized by impact and implementation difficulty. This audit forms the foundation of our SEO strategy and helps us identify quick wins and long-term opportunities specific to your business."
    },
    {
      question: "How long does it take to see results from SEO?",
      answer: "SEO is a long-term strategy. While some improvements may be visible within 1-2 months, significant results typically take 3-6 months depending on your industry, competition, and website history. We provide regular updates on progress metrics throughout the process. For highly competitive keywords, the timeline may extend to 6-12 months, but the sustainable traffic gains are worth the investment."
    },
    {
      question: "Do you offer month-to-month contracts or require long-term commitments?",
      answer: "We offer flexible engagement options. While we recommend a minimum 6-month commitment for SEO campaigns to produce meaningful results, we do offer month-to-month arrangements with a slightly higher monthly rate to accommodate your business needs. Our clients typically stay with us for years because of the ongoing value we provide, but we don't lock you into lengthy contracts."
    },
    {
      question: "How do you measure and report on SEO performance?",
      answer: "We track key performance indicators including organic traffic, keyword rankings, conversion rates, backlink quality/quantity, and engagement metrics. You'll receive detailed monthly reports and have access to a real-time dashboard showing all relevant metrics. Our reporting combines technical data with business insights, so you understand not just what's happening but why it matters for your bottom line."
    },
    {
      question: "What makes Los Angeles SEO Group different from other agencies?",
      answer: "We combine data-driven strategies with creative content approaches, maintain complete transparency in our work, employ specialized teams for different industry verticals, and customize every campaign based on your specific business goals rather than using a one-size-fits-all approach. Our team includes experienced professionals with backgrounds in different industries, allowing us to understand your business challenges beyond just SEO."
    },
    {
      question: "Do you guarantee rankings or specific results?",
      answer: "We don't guarantee specific rankings because search engines frequently update their algorithms and no one can control Google. Instead, we guarantee our methodology, expertise, and commitment to achieving the best possible results for your business. We set realistic expectations based on our experience and focus on metrics that impact your business growth, not just vanity metrics."
    },
    {
      question: "How often will we communicate about the campaign?",
      answer: "Communication frequency depends on your package and preferences. Typically, we schedule monthly strategy calls to review progress and discuss next steps. For more competitive packages, we offer bi-weekly calls. You'll also receive monthly reports, and our team is available via email or phone for questions that arise between scheduled meetings. We pride ourselves on responsiveness and clear communication."
    },
    {
      question: "Can you work with our in-house marketing team?",
      answer: "Absolutely! We frequently collaborate with in-house marketing teams, providing specialized SEO expertise while integrating with your existing workflows and strategies. We can train your team, provide guidance on SEO best practices, and even create custom workflows that divide responsibilities effectively between our team and yours. Many clients find this collaborative approach maximizes results."
    },
    {
      question: "What if we want to pause or cancel services?",
      answer: "We understand business needs change. If you need to pause services, we require 30 days' notice, and we can hold your spot for up to 3 months. For cancellations, we also require 30 days' notice. We'll provide a comprehensive handover document with recommendations for maintaining SEO progress. Many clients return after realizing the value we provide, and we welcome them back without startup fees."
    },
    {
      question: "Do you offer any guarantees or refunds?",
      answer: "We offer a 30-day satisfaction guarantee on our work quality and deliverables. If you're not satisfied with the quality of our work within the first 30 days, we'll correct any issues at no additional cost or refund your initial payment. Beyond this period, we don't offer refunds, but we're committed to resolving any concerns to ensure your satisfaction with our services."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        seo={{
          title: "Pricing | Los Angeles SEO Group",
          description: "View our transparent pricing packages for SEO services tailored to your business needs - from local businesses to enterprise solutions.",
          canonical: "https://losangelesseogroup.com/pricing",
        }}
        url="https://losangelesseogroup.com/pricing"
      />
      <Navbar />
      
      <PageTransition>
        <main className="flex-grow">
          <section className="pt-28 pb-12 md:pt-32 md:pb-16 bg-gradient-to-b from-background to-muted/30">
            <div className="page-container">
              <div className="max-w-3xl mx-auto text-center">
                <motion.h1 
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading"
                >
                  Transparent SEO Pricing for 
                  <span className="text-primary block mt-2">Measurable Results</span>
                </motion.h1>
                <motion.p 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mt-4 md:mt-6 text-lg text-muted-foreground"
                >
                  No hidden fees or complex packages. Choose the right plan for your business 
                  and start climbing the search rankings today.
                </motion.p>
              </div>
            </div>
          </section>
          
          <section className="py-12 md:py-16 bg-muted/30">
            <div className="page-container">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {pricingData.map((plan) => (
                  <PricingCard 
                    key={plan.id}
                    {...plan}
                    onSelect={() => handlePlanSelect(plan.id)}
                  />
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-card rounded-xl border border-border max-w-xl mx-auto">
                <p className="text-sm text-muted-foreground mb-4">
                  * Based on industry, we can let you know how many locations qualify
                </p>
              </div>

              <div className="mt-12 text-center">
                <p className="text-muted-foreground">
                  Need a custom solution? <a href="/contact" className="text-primary font-medium hover:underline">Contact us</a> for a personalized quote.
                </p>
              </div>
            </div>
          </section>
          
          <section className="py-12 md:py-16 bg-muted/30">
            <div className="page-container">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold font-heading">Need More Locations?</h2>
                  <p className="mt-3 text-muted-foreground">
                    Scale your SEO campaign across additional locations
                  </p>
                </div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="bg-card rounded-xl p-8 shadow-lg border border-border"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">Additional Locations</h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        After your first 3 locations (included in Medium Competitive Keywords package), 
                        add more locations at $950 per location per month.
                      </p>
                      
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium">Number of additional locations:</label>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAdditionalLocations(Math.max(0, additionalLocations - 1))}
                            disabled={additionalLocations === 0}
                          >
                            -
                          </Button>
                          <span className="w-12 text-center font-bold">{additionalLocations}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAdditionalLocations(additionalLocations + 1)}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="bg-primary/5 rounded-lg p-6 text-center min-w-[200px]">
                      <div className="text-sm text-muted-foreground mb-1">Additional Monthly Cost</div>
                      <div className="text-3xl font-bold text-primary">
                        ${(additionalLocations * 950).toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        {additionalLocations} location{additionalLocations !== 1 ? 's' : ''} × $950/mo
                      </div>
                    </div>
                  </div>

                  {additionalLocations > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-6 pt-6 border-t border-border"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Total with {additionalLocations + 3} location{additionalLocations + 3 !== 1 ? 's' : ''}:</span>
                        <span className="text-2xl font-bold">
                          ${(2000 + additionalLocations * 950).toLocaleString()}/month
                        </span>
                      </div>
                      <Button 
                        className="w-full mt-4" 
                        size="lg"
                        onClick={() => handlePlanSelect('medium')}
                      >
                        Get Started with {additionalLocations + 3} Location{additionalLocations + 3 !== 1 ? 's' : ''}
                      </Button>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-gradient-to-b from-background to-muted/30">
            <div className="page-container">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold font-heading">How We Deliver Results</h2>
                <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                  Our proven four-step process ensures your SEO campaign delivers measurable ROI
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  {
                    step: 1,
                    title: "Discovery & Audit",
                    description: "We analyze your site, industry, competitors, and identify opportunities."
                  },
                  {
                    step: 2,
                    title: "Strategy Development",
                    description: "Custom roadmap based on audit findings, with clear milestones and KPIs."
                  },
                  {
                    step: 3,
                    title: "Implementation",
                    description: "Our team executes the strategy with technical fixes, content and link building."
                  },
                  {
                    step: 4,
                    title: "Measurement & Refinement",
                    description: "Continuous monitoring and optimization based on performance data."
                  }
                ].map((item) => (
                  <motion.div
                    key={item.step}
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: item.step * 0.1 }}
                    className="relative"
                  >
                    <div className="bg-card rounded-lg p-6 h-full shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <span className="font-bold text-primary">{item.step}</span>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
          
          <section className="py-12 md:py-16 bg-muted/30">
            <div className="page-container">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading">Frequently Asked Questions</h2>
                <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                  Find answers to common questions about our SEO services and approach
                </p>
              </div>
              
              <div className="max-w-3xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.slice(0, 5).map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left hover:text-primary transition-colors">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground">
                          {faq.answer}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                <div className="mt-8 text-center">
                  <Button asChild variant="outline" className="flex items-center gap-2">
                    <Link to="/faqs">
                      <HelpCircle className="w-4 h-4" />
                      <span>View All FAQs</span>
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
          
          <section className="py-16 bg-gradient-to-r from-muted/40 to-background">
            <div className="page-container">
              <div className="max-w-4xl mx-auto bg-card border border-border rounded-xl p-8 shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-2xl font-bold mb-4">Need Help Choosing?</h2>
                    <p className="text-muted-foreground mb-6">
                      Our team can help you select the right SEO package based on your specific business goals, competition, and budget.
                    </p>
                    <ul className="space-y-3 mb-6">
                      {[
                        "Free consultation with an SEO specialist",
                        "Competitor analysis to understand your market",
                        "Website audit to identify opportunities",
                        "Custom strategy recommendation",
                        "Flexible pricing options available"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start">
                          <Check className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button asChild className="bg-primary-gradient">
                      <Link to="/contact">
                        Schedule a Free Consultation
                      </Link>
                    </Button>
                  </div>
                  
                  <div className="bg-muted/30 rounded-lg p-6 flex flex-col justify-center">
                    <div className="text-center mb-4">
                      <h3 className="font-bold text-xl mb-2">Our Services</h3>
                      <p className="text-sm text-muted-foreground mb-6">
                        Explore our specialized SEO services
                      </p>
                    </div>
                    
                    <ul className="space-y-3">
                      {pricingData.map((service, index) => (
                        <li key={index}>
                          <Link
                            to={service.link}
                            className="flex items-center justify-between p-3 bg-background/70 hover:bg-background rounded-lg transition-colors group"
                          >
                            <span className="font-medium">{service.title}</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </PageTransition>
      
      <Dialog open={showOnboarding} onOpenChange={setShowOnboarding}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="space-y-4">
            <SeoOnboardingForm 
              selectedPlan={selectedPlan || ''} 
              onSubmit={handleOnboardingSubmit}
              onClose={() => setShowOnboarding(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default Pricing;
